import { Component, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { form, min, required, FormField, maxLength } from '@angular/forms/signals';
import { UiDialog, UiSelect } from '@shared/components/ui';
import { ModalConfirmacion } from '../modal-confirmacion/modal-confirmacion';
import { ToastService } from '@shared/services/toast.service';
import { FormErrorMessagePipe } from '../../pipes/form-error-message.pipe';
import { TipoPagoService } from '@shared/services/tipo-pago.service';
import { map } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { IFormMontoDescripcion } from '@core/interfaces/form-monto-descripcion.interface';

@Component({
  selector: 'app-form-monto-descripcion',
  imports: [UiDialog, ModalConfirmacion, FormField, FormErrorMessagePipe, UiSelect, AsyncPipe],
  templateUrl: './form-monto-descripcion.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './form-monto-descripcion.css',
})
export class FormMontoDescripcion {
  // servicios
  private toastService = inject(ToastService);
  private tipoPagoService = inject(TipoPagoService);
  // datos
  form_titulo = input.required<string>();
  form_monto_texto = input.required<string>();
  form_descripcion_texto = input.required<string>();
  form_descripcion_opcional = input<boolean>(true);
  form_descripcion_maximo_caracteres = input<number>(125);
  form_boton_confirmar_texto = input.required<string>();
  form_boton_cancelar_texto = input.required<string>();
  form_anuncio = input<string>('');
  // inputs para confirmar
  tiene_confirmacion = input<boolean>(true);
  tiene_tipo_pago = input<boolean>(false);
  confirmar_titulo = input<string>('Confirmar acción');
  confirmar_texto = input<string>('¿Estás seguro de que deseas continuar con esta acción?');
  confirmar_boton_confirmar_texto = input<string>('Sí, confirmar');
  confirmar_boton_cancelar_texto = input<string>('Cancelar');

  optionsTipoPago$ = this.tipoPagoService.listTiposPago.pipe(
    map((tiposPago) => tiposPago.map((tipoPago) => ({ label: tipoPago.nombre, value: tipoPago }))),
  );

  // formulario
  formApertura = form(
    signal({
      monto: 0,
      descripcion: '',
      tipo_pago: null,
    }),
    (x) => {
      required(x.monto, { message: 'El monto inicial es obligatorio' });
      min(x.monto, 0, { message: 'El monto inicial no puede ser negativo' });
      required(x.descripcion, {
        message: 'La descripción es obligatoria',
        when: () => this.form_descripcion_opcional() == false,
      });
      required(x.tipo_pago, {
        message: 'El tipo de pago es obligatorio',
        when: () => this.tiene_tipo_pago() == true,
      });
      maxLength(x.descripcion, this.form_descripcion_maximo_caracteres(), {
        message: `La descripción no puede exceder los ${this.form_descripcion_maximo_caracteres()} caracteres`,
      });
    },
  );
  modal_confirmar = signal(false);
  cerrar = output<IFormMontoDescripcion | undefined>();

  // métodos
  private resetForm() {
    this.formApertura().value.set({
      monto: 0,
      descripcion: '',
      tipo_pago: null,
    });
  }
  private markAsAllTouched() {
    this.formApertura.monto().markAsTouched();
    this.formApertura.descripcion().markAsTouched();
    if (this.tiene_tipo_pago()) {
      this.formApertura.tipo_pago().markAsTouched();
    }
  }
  private checkInvalidForm(): boolean {
    if (this.formApertura().invalid()) {
      this.markAsAllTouched();
      this.toastService.show(
        'top-right',
        'warn',
        'Formulario inválido',
        'Por favor, revise los campos.',
      );
      return true;
    }
    return false;
  }
  confirmar() {
    if (this.checkInvalidForm()) return;
    const { monto: monto_inicial, descripcion, tipo_pago } = this.formApertura().controlValue();
    this.cerrar.emit({ monto: monto_inicial, descripcion, tipo_pago });
    this.resetForm();
    this.modal_confirmar.set(false);
  }
  onCerrar() {
    this.resetForm();
    this.cerrar.emit(undefined);
    this.modal_confirmar.set(false);
  }
}
